import { httpClient } from './http-client';
import type { ApiResponse, PaginatedResponse } from './api.types';
import type { BulkImportRequest } from '@/modelos/Cliente';
import type { ImportacionMasiva } from '@/modelos/ImportacionMasiva';

export interface ClienteBusquedaDto {
  id: string;
  numeroDocumento: string;
  nombreCompleto: string | null;
  razonSocial: string | null;
}

export interface ClienteDto {
  id: string;
  empresaId: string;
  tipoIdentidadCodigo: string | null;
  tipoIdentidadNombre: string | null;
  numeroDocumento: string | null;
  tipoPersona: string | null;
  tipoCuenta: string | null;
  razonSocial: string | null;
  nombreComercial: string | null;
  primerNombre: string | null;
  segundoNombre: string | null;
  apellidoPaterno: string | null;
  apellidoMaterno: string | null;
  nombreCompleto: string | null;
  correosElectronicos: string | null;
  telefonos: string | null;
  paginaWeb: string | null;
  pais: string | null;
  departamentoCodigo: string | null;
  departamento: string | null;
  provinciaCodigo: string | null;
  provincia: string | null;
  distritoCodigo: string | null;
  distrito: string | null;
  ubigeo: string | null;
  direccion: string | null;
  referenciaDireccion: string | null;
  esActivo: boolean;
  esPorDefecto: boolean;
  motivoDeshabilitacion: string | null;
  tipoContribuyente: string | null;
  estadoContribuyente: string | null;
  condicionDomicilio: string | null;
  fechaInscripcion: string | null;
  actividadesEconomicas: string | null;
  sistemaEmision: string | null;
  esEmisorElectronico: boolean;
  esCpeHabilitado: boolean;
  cpeHabilitadoLista: string | null;
  esAgenteRetencion: boolean;
  esAgentePercepcion: boolean;
  esBuenContribuyente: boolean;
  formaPago: string | null;
  formaPagoId: string | null;
  formaPagoNombre: string | null;
  monedaCodigo: string | null;
  monedaNombre: string | null;
  listaPrecio: string | null;
  usuarioAsignadoId: string | null;
  usuarioAsignadoNombre: string | null;
  esExceptuadaPercepcion: boolean;
  observaciones: string | null;
  contactos: string | null;
  direcciones: string | null;
  usuarioId: string | null;
  usuarioNombre: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface ClienteInputDto {
  empresaId: string;
  tipoIdentidadCodigo?: string | null;
  tipoIdentidadNombre?: string | null;
  numeroDocumento?: string | null;
  tipoPersona?: string | null;
  tipoCuenta?: string | null;
  razonSocial?: string | null;
  nombreComercial?: string | null;
  primerNombre?: string | null;
  segundoNombre?: string | null;
  apellidoPaterno?: string | null;
  apellidoMaterno?: string | null;
  nombreCompleto?: string | null;
  correosElectronicos?: string | null;
  telefonos?: string | null;
  paginaWeb?: string | null;
  pais?: string | null;
  departamentoCodigo?: string | null;
  departamento?: string | null;
  provinciaCodigo?: string | null;
  provincia?: string | null;
  distritoCodigo?: string | null;
  distrito?: string | null;
  ubigeo?: string | null;
  direccion?: string | null;
  referenciaDireccion?: string | null;
  esActivo?: boolean;
  esPorDefecto?: boolean;
  motivoDeshabilitacion?: string | null;
  tipoContribuyente?: string | null;
  estadoContribuyente?: string | null;
  condicionDomicilio?: string | null;
  fechaInscripcion?: string | null;
  actividadesEconomicas?: string | null;
  sistemaEmision?: string | null;
  esEmisorElectronico?: boolean;
  esCpeHabilitado?: boolean;
  cpeHabilitadoLista?: string | null;
  esAgenteRetencion?: boolean;
  esAgentePercepcion?: boolean;
  esBuenContribuyente?: boolean;
  formaPago?: string | null;
  formaPagoId?: string | null;
  formaPagoNombre?: string | null;
  monedaCodigo?: string | null;
  monedaNombre?: string | null;
  listaPrecio?: string | null;
  usuarioAsignado?: string | null;
  esExceptuadaPercepcion?: boolean;
  observaciones?: string | null;
  contactos?: string | null;
  direcciones?: string | null;
  usuarioId?: string | null;
  usuarioNombre?: string | null;
}

export const clientesService = {
  getAll: (params?: { page?: number; pageSize?: number; search?: string; tipoCuenta?: string; esActivo?: boolean }): Promise<ApiResponse<PaginatedResponse<ClienteDto>>> => {
    return httpClient.get('/clientes', params);
  },

  getById: (id: string): Promise<ApiResponse<ClienteDto>> => {
    return httpClient.get(`/clientes/${id}`);
  },

  getByNumeroDocumento: (numeroDocumento: string): Promise<ApiResponse<ClienteDto>> => {
    return httpClient.get(`/clientes/documento/${numeroDocumento}`);
  },

  buscarPorDocumentos: (numerosDocumento: string[]): Promise<ApiResponse<ClienteBusquedaDto[]>> => {
    return httpClient.post('/clientes/buscar-por-documentos', { numerosDocumento });
  },

  create: (data: ClienteInputDto): Promise<ApiResponse<ClienteDto>> => {
    return httpClient.post('/clientes', data);
  },

  update: (id: string, data: ClienteInputDto): Promise<ApiResponse<ClienteDto>> => {
    return httpClient.put(`/clientes/${id}`, data);
  },

  delete: (id: string): Promise<ApiResponse<void>> => {
    return httpClient.delete(`/clientes/${id}`);
  },

  toggleStatus: (id: string): Promise<ApiResponse<ClienteDto>> => {
    return httpClient.patch(`/clientes/${id}/toggle-status`);
  },

  buscar: (query: string, pageSize = 10, tipoCuenta?: string): Promise<ApiResponse<PaginatedResponse<ClienteDto>>> => {
    return httpClient.get('/clientes', { search: query, pageSize, page: 1, tipoCuenta });
  },

  getPorDefecto: (): Promise<ApiResponse<ClienteDto>> =>
    httpClient.get('/clientes/por-defecto'),

  getClienteGeneral: (): Promise<ApiResponse<ClienteDto>> =>
    httpClient.get('/clientes/documento/00000000'),

  bulkImport: (data: BulkImportRequest): Promise<ApiResponse<ImportacionMasiva>> => {
    const registros = data.registros.map((dto) => ({
      tipoDocumento: dto.tipoDocumento,
      numeroDocumento: dto.numeroDocumento,
      tipoPersona: dto.tipoPersona,
      tipoCuenta: dto.tipoCuenta ?? dto.type,
      razonSocial: dto.razonSocial,
      nombreComercial: dto.nombreComercial,
      primerNombre: dto.primerNombre,
      segundoNombre: dto.segundoNombre,
      apellidoPaterno: dto.apellidoPaterno,
      apellidoMaterno: dto.apellidoMaterno,
      nombreCompleto: dto.nombreCompleto,
      emails: dto.emails,
      telefonos: dto.telefonos,
      pais: dto.pais,
      departamento: dto.departamento,
      provincia: dto.provincia,
      distrito: dto.distrito,
      ubigeo: dto.ubigeo,
      direccion: dto.direccion,
      referenciaDireccion: dto.referenciaDireccion,
      esActivo: dto.estadoCliente !== 'Deshabilitado',
      motivoDeshabilitacion: dto.motivoDeshabilitacion,
    }));
    return httpClient.post('/clientes/importar', { modo: data.modo, registros, consultarDocumento: data.consultarDocumento ?? false });
  },

  downloadExcel: (params?: {
    modo?: 'basico' | 'completo';
    search?: string;
    tipoCuenta?: string;
    esActivo?: boolean;
  }): Promise<Blob> => {
    const query: Record<string, string | number | boolean | undefined> = {
      modo: params?.modo,
      search: params?.search,
      tipoCuenta: params?.tipoCuenta,
      esActivo: params?.esActivo,
    };
    return httpClient.obtenerBlob('/reportes/clientes/excel', query);
  },
};
